import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import b from 'b_';
import ConsoleLoginStore from '../../stores/ConsoleLoginStore';
import FlipNext from '../Animation/FlipNext';
import { FTBody, FTHeadline, FTInput } from '../Fuse';
import FuseButton from '../FuseControls/FuseButton';
import { AuthPage, AuthBox } from './AuthLayout';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseTextfield from '../FuseControls/FuseTextfield';
import './ConsoleLogin.css';

const block = b.lock('ConsoleLogin');

/**
 * @property {ConsoleLoginStore} props.consoleLogin
 */
class ConsoleLogin extends Component {
    static propTypes = {
        consoleLogin: ConsoleLoginStore.PropType.isRequired,
    };

    isUnmounted = false;

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    get isPreLoginSubmitted() {
        return this.props.consoleLogin.apiEmailSubmitState.successCount > 0;
    }

    onPreLoginSubmit = (event) => {
        event.preventDefault();
        this.props.consoleLogin.preLoginSubmit()
            .then(() => this.props.consoleLogin.password.focus())
            .catch(() => this.props.consoleLogin.email.focus());
    };

    onLoginSubmit = (event) => {
        event.preventDefault();
        this.props.consoleLogin.submit()
            .then(() => {
                if (!this.isUnmounted) {
                    let redirectUrl = '/console/index';
                    if (['/console/forgot', // exclude those paths from redirect
                        '/console/sso/error'].indexOf(this.props.consoleLogin.redirectUrl) === -1) {
                        redirectUrl = this.props.consoleLogin.redirectUrl;
                    }
                    window.location = redirectUrl;
                }
            })
            .catch(() => { /* error is displayed in label */ });
    };

    getEmailErrorLabel() {
        if (!this.props.consoleLogin.email.isTouched && !this.props.consoleLogin.email.isValid) {
            return 'Your email address is invalid.';
        }
        return '';
    }

    getPasswordErrorLabel() {
        const { email, password, isServerValidationFailed } = this.props.consoleLogin;
        if (isServerValidationFailed && !email.isTouched && !password.isTouched) {
            return 'Your email/password combination is invalid.';
        }
        return '';
    }

    getError() {
        if (this.getEmailErrorLabel() !== '') {
            return this.getEmailErrorLabel();
        }

        if (this.getPasswordErrorLabel() !== '') {
            return this.getPasswordErrorLabel();
        }
        return null;
    }

    getRenderAlert() {
        const { consoleLogin } = this.props;
        let bodyText = consoleLogin.isAccountLocked ? 'Account locked. Too many unsuccessful ' +
            `login attempts, please try again in ${consoleLogin.accountLockoutMin} minutes.` : null;

        const error = this.getError();
        if (error !== null) {
            bodyText = error;
        }

        if (bodyText === null) {
            return null;
        }
        return <FuseAlert
            className={block('MarginBottom', { section: 'alert' })}
            type="danger"
            icon={consoleLogin.isAccountLocked ? 'lock' : 'error'}
            centerIcon
        >
            <FTInput className={block('AlertText')}>{bodyText}</FTInput>
        </FuseAlert>;
    }

    renderLogin() {
        const { consoleLogin } = this.props;
        return <AuthBox
            onSubmit={this.onLoginSubmit}
            className={block('Display')}
        >
            <FTHeadline style={{ marginBottom: 20 }}>
                Welcome to Isolation Console
            </FTHeadline>
            {consoleLogin.isSSO.isTrue && <Fragment>
                <FuseButton
                    type={'primary'}
                    color={'blue'}
                    className={block('MarginBottom')}
                    wrapperClassName={block('SSO_Button')}
                    fullWidth
                    onClick={consoleLogin.onSSOButtonClick}
                >
                    Log in with SSO
                </FuseButton>
                <FTBody className={block('MarginBottom', { section: 'lineDivider' })}><span>OR</span></FTBody>
            </Fragment>}
            <FTBody className={block('MarginBottom', { section: 'textBody' })}>
                Log in with your Proofpoint Isolation credentials.
            </FTBody>
            {this.getRenderAlert()}
            <FuseTextfield
                store={consoleLogin.email}
                placeholder="Company email address"
                className={block('MarginBottom', { section: 'textfield' })}
                error={this.getError() !== null}
                type="email"
                name="email"
                autoComplete="email"
            />
            <FuseTextfield
                store={consoleLogin.password}
                placeholder="Enter password"
                className={block('MarginBottom', { section: 'textfield' })}
                rightIcon={consoleLogin.passwordVisibility.isTrue ? 'visibility_off' : 'visibility'}
                rightIconClassName={block('VisibilityIcon')}
                onRightIconClick={consoleLogin.passwordVisibility.toggle}
                type={consoleLogin.passwordVisibility.isTrue ? 'text' : 'password'}
                error={this.getPasswordErrorLabel() !== ''}
                name="password"
                autoComplete="current-password"
            />
            <FuseButton
                submit
                type={'primary'}
                color={'blue'}
                className={block('MarginBottom')}
                fullWidth
                disabled={!consoleLogin.canSubmit}
                loading={consoleLogin.apiSubmitState.isLoading}
                onClick={this.onLoginSubmit}
            >Login</FuseButton>
            <FTInput className={block('ForgotLinkWrapper')}>
                <a
                    className={block('ForgotLink')}
                    href="/console/forgot"
                >Forgot password</a>
            </FTInput>
        </AuthBox>;
    }

    renderPreLoginAlert() {
        const bodyText = this.getEmailErrorLabel();

        if (bodyText === '') {
            return null;
        }

        return <FuseAlert
            className={block('MarginBottom', { section: 'alert' })}
            type="danger"
            icon="error"
            centerIcon
        >
            <FTInput className={block('AlertText')}>{bodyText}</FTInput>
        </FuseAlert>;
    }

    renderPreLogin() {
        const { consoleLogin } = this.props;
        return <AuthBox
            className={block('Display')}
            onSubmit={this.onPreLoginSubmit}
        >
            <FTHeadline style={{ marginBottom: 10 }}>
                Welcome to Isolation Console
            </FTHeadline>
            <FTBody className={block('MarginBottom', { section: 'textBody' })}>
                Please enter your company email address.
            </FTBody>
            {this.renderPreLoginAlert()}
            <FuseTextfield
                className={block('MarginBottom', { section: 'textfield' })}
                placeholder="Company email address"
                store={consoleLogin.email}
                error={this.getEmailErrorLabel() !== ''}
                type="email"
                name="email"
                autoComplete="email"
                autoFocus
            />
            <FuseTextfield
                className={block('HiddenPassword')}
                placeholder="hidden password field for password managers"
                store={consoleLogin.password}
                type="password"
                name="password"
                autoComplete="current-password"
                inert="true"
            />
            <FuseButton
                submit
                type="primary"
                color="blue"
                fullWidth
                disabled={!consoleLogin.canSubmitPreLogin}
                loading={consoleLogin.apiEmailSubmitState.isLoading}
                onClick={this.onPreLoginSubmit}
            >Next</FuseButton>
        </AuthBox>;
    }

    render() {
        return <AuthPage>
            <FlipNext flip={this.isPreLoginSubmitted}>
                {this.isPreLoginSubmitted
                    ? this.renderLogin()
                    : this.renderPreLogin()}
            </FlipNext>
        </AuthPage>;
    }
}

export default translate()(inject('consoleLogin')(observer(ConsoleLogin)));
